import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { robots } from "@/api/robots";
import { tearsheet } from "@/api/tearsheet";
import { Sparkline } from "@/components/Sparkline";
import { Card, CardBody, Label, Select } from "@/components/ui";

function fmtPct(n: number | undefined): string {
  return n == null || !Number.isFinite(n) ? "—" : `${n.toFixed(2)}%`;
}
function fmtNum(n: number | undefined, digits = 2): string {
  return n == null || !Number.isFinite(n)
    ? "—"
    : n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

export function TearsheetPage() {
  const [robotId, setRobotId] = useState<string>("");
  const robotList = useQuery({ queryKey: ["robots"], queryFn: () => robots.list() });
  const q = useQuery({
    queryKey: ["tearsheet", robotId || "all"],
    queryFn: () => tearsheet.load(robotId || undefined),
  });

  if (q.isLoading) {
    return <p className="text-sm text-term-mute">Loading tearsheet…</p>;
  }
  if (q.isError || !q.data) {
    return <p className="text-sm text-term-mute">Could not load tearsheet.</p>;
  }
  const { metrics, equity_curve, monthly_returns, drawdowns } = q.data;
  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-term-fg">Tearsheet</h1>
          <p className="text-2xs text-term-mute">
            Full performance report computed off the closed-position equity
            curve. Pick a robot to filter to a single bot's trades, or leave
            blank for engine-wide.
          </p>
        </div>
        <div className="min-w-[12rem]">
          <Label>Robot</Label>
          <Select value={robotId} onChange={(e) => setRobotId(e.target.value)}>
            <option value="">All (engine-wide)</option>
            {(robotList.data?.robots ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Total return" value={fmtPct(metrics.total_return_pct)} />
        <Kpi label="Annualized" value={fmtPct(metrics.annualized_return_pct)} />
        <Kpi label="Sharpe" value={fmtNum(metrics.sharpe_ratio, 2)} />
        <Kpi label="Sortino" value={fmtNum(metrics.sortino_ratio, 2)} />
        <Kpi label="Calmar" value={fmtNum(metrics.calmar_ratio, 2)} />
        <Kpi label="Max DD" value={fmtPct(metrics.max_drawdown_pct)} />
        <Kpi label="Win rate" value={fmtPct(metrics.win_rate)} />
        <Kpi label="Profit factor" value={fmtNum(metrics.profit_factor, 2)} />
      </div>

      <Card>
        <CardBody>
          <p className="text-2xs uppercase tracking-wider text-term-mute">Equity curve</p>
          <Sparkline data={equity_curve} width={720} height={120} />
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          <div className="border-b border-term-border bg-term-elev px-3 py-1.5">
            <p className="text-2xs font-semibold uppercase text-term-mute">Monthly returns</p>
          </div>
          {Object.keys(monthly_returns).length === 0 ? (
            <p className="p-3 text-sm text-term-mute">No closed trades yet.</p>
          ) : (
            <table className="w-full text-xs">
              <tbody>
                {Object.entries(monthly_returns).map(([month, ret]) => (
                  <tr key={month} className="border-t border-term-border">
                    <td className="px-3 py-1.5 text-term-dim">{month}</td>
                    <td
                      className={`px-3 py-1.5 text-right ${
                        ret >= 0 ? "text-emerald-400" : "text-red-400"
                      }`}
                    >
                      {fmtPct(ret)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {drawdowns.length > 0 && (
        <Card>
          <CardBody className="p-0">
            <div className="border-b border-term-border bg-term-elev px-3 py-1.5">
              <p className="text-2xs font-semibold uppercase text-term-mute">Top drawdowns</p>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase">#</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Depth</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase">Duration (bars)</th>
                </tr>
              </thead>
              <tbody>
                {drawdowns.map((d, i) => (
                  <tr key={i} className="border-t border-term-border">
                    <td className="px-3 py-1.5 text-term-dim">{i + 1}</td>
                    <td className="px-3 py-1.5 text-right text-red-400">
                      {fmtPct(d.depth_pct)}
                    </td>
                    <td className="px-3 py-1.5 text-right text-term-dim">{d.duration}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardBody>
        <p className="text-2xs uppercase tracking-wider text-term-mute">{label}</p>
        <p className="mt-1 text-lg font-semibold text-term-fg">{value}</p>
      </CardBody>
    </Card>
  );
}
